/**
 * Profile Context
 * Manages user profile data globally across the application
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';
import { getProfile, onTokenRefresh, onLogout, isAuthenticated } from '../services/auth.service';
import type { ProfileResponse } from '../types/auth.types';

interface ProfileContextValue {
  profile: ProfileResponse | null;
  loading: boolean;
  error: Error | null;
  refreshProfile: () => Promise<void>;
  clearProfile: () => void;
}

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);

interface ProfileProviderProps {
  readonly children: ReactNode;
}

/**
 * Profile Provider
 * Fetches and stores user profile data
 * Automatically fetches profile on mount if user is authenticated
 */
export function ProfileProvider({ children }: ProfileProviderProps) {
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const refreshProfile = useCallback(async (): Promise<void> => {
    // Only fetch if authenticated
    if (!isAuthenticated()) {
      setProfile(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getProfile();
      setProfile(data);
    } catch (err) {
      setError(err as Error);
      setProfile(null);
      // eslint-disable-next-line no-console
      console.error('Failed to fetch profile:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearProfile = useCallback((): void => {
    setProfile(null);
    setError(null);
  }, []);

  // Fetch profile on mount if authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      refreshProfile();
    }

    // Register callback to refresh profile after token refresh
    const unregisterRefresh = onTokenRefresh(() => {
      refreshProfile();
    });

    // Register callback to clear profile after logout
    const unregisterLogout = onLogout(() => {
      clearProfile();
    });

    // Cleanup on unmount
    return () => {
      unregisterRefresh();
      unregisterLogout();
    };
  }, [refreshProfile, clearProfile]);

  const value: ProfileContextValue = useMemo(
    () => ({
      profile,
      loading,
      error,
      refreshProfile,
      clearProfile,
    }),
    [profile, loading, error, refreshProfile, clearProfile],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

/**
 * Hook to access profile context
 * Must be used within ProfileProvider
 */
export function useProfile(): ProfileContextValue {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
}
